import { useState } from 'react';
import { useSnapshot } from 'valtio';

import * as navigationStore from '@cartwhl/common/stores/navigationStore';
import * as updatedProductStore from '@cartwhl/common/stores/updatedProductStore';
import * as userStore from '@cartwhl/common/stores/userStore';
import { getAppletsUrl, getHomepageUrl } from '@cartwhl/common/utils';
import {
  formatOrgCreditsForDisplay,
  getOrgAvailableCredits,
  isOrgCreditsWorkspace,
} from '@cartwhl/common/utils/organizationWorkspace';
import Avatar from '@cartwhl/ui/atoms/Avatar';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Text from '@cartwhl/ui/base/TextNew';
import BlueDotIcon from '@cartwhl/ui/icons/BlueDotIcon';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import LightDarkIcon from '@cartwhl/ui/icons/LightDarkIcon';
import LikeIcon from '@cartwhl/ui/icons/LikeIcon';
import LogoutIcon from '@cartwhl/ui/icons/LogoutIcon';
import PenIcon from '@cartwhl/ui/icons/PenIcon';
import UserIcon from '@cartwhl/ui/icons/UserIcon';

import SpinIcon from '../icons/SpinIcon';
import Button from '../input/Button';

export const CDN_BASE = 'https://resources.getcartwheel.com/share/demo-videos';
const USER_MENU_DROPDOWN_Z_INDEX = 60;

interface UserMenuProps {
  inModal?: boolean;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
}

export default function UserMenu({
  inModal = false,
  side = 'bottom',
  align = 'end',
}: UserMenuProps) {
  const { userInfo, theme, organizationInfo } = useSnapshot(userStore.state);
  const [payNowLoading, setPayNowLoading] = useState(false);

  const orgCreditsWorkspace = isOrgCreditsWorkspace(organizationInfo);
  const orgAvailableCredits = getOrgAvailableCredits(organizationInfo);

  return (
    <Dropdown
      inModal={inModal}
      side={side}
      align={align}
      zIndex={inModal ? undefined : USER_MENU_DROPDOWN_Z_INDEX}
      trigger={
        <div className="hover:bg-gray-15 data-[state=open]:bg-gray-15 hover:dark:bg-gray-75 data-[state=open]:dark:bg-gray-90 outline-hidden flex h-9 w-9 cursor-pointer items-center justify-center rounded-full transition-colors">
          <Avatar userInfo={userInfo} />
        </div>
      }
    >
      <div className="styled-scrollbar max-h-[calc(var(--radix-dropdown-menu-content-available-height)-1rem)] overflow-y-auto overflow-x-hidden overscroll-contain">
        <div className="hover:bg-gray-15 hover:dark:bg-gray-90 m-2 h-[120px] rounded-md">
          <a
            className="relative flex h-full w-full flex-col items-center justify-center"
            href={getHomepageUrl('/profile/account-details')}
          >
            <Avatar userInfo={userInfo} size={12} className="relative mb-3">
              <span className="dark:bg-gray-90 absolute -bottom-1 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-white">
                <PenIcon />
              </span>
            </Avatar>
            <Text token="14/medium" className="">
              {userInfo.firstName} {userInfo.lastName}
            </Text>
            <Text token="12" className="text-gray-45">
              {userInfo.email}
            </Text>
            <Text token="12" className="text-gray-45">
              {userInfo.planType}
            </Text>
          </a>
        </div>
        <div className="flex items-center justify-center">
          <a
            href={getHomepageUrl('/profile/plan-billing')}
            className="border-primary hover:bg-gray-15 inline-flex h-10 items-center gap-2 rounded-md border px-3 py-2"
          >
            <SpinIcon className="h-5 w-5 shrink-0" />
            <Text token="14/medium" leading="title">
              {orgCreditsWorkspace
                ? `${formatOrgCreditsForDisplay(orgAvailableCredits)} credits`
                : `${userInfo.spinsAvailable} Spins`}
            </Text>
          </a>
        </div>
        <div className="mb-3 mt-2 flex items-center justify-center">
          {orgCreditsWorkspace ? (
            <a
              href={getHomepageUrl('/pricing#pricing-credits-explainer')}
              className="text-gray-45 underline"
            >
              <Text token="12">How credits work</Text>
            </a>
          ) : (
            <button
              className="text-gray-45 cursor-pointer underline"
              onClick={() => {
                navigationStore.setModal(
                  navigationStore.CommonModalName.EXPLAIN_SPINS,
                );
              }}
            >
              <Text token="12">What are Spins?</Text>
            </button>
          )}
        </div>
        {organizationInfo?.inStripeFreeTrialPeriod === true && (
          <>
            {/* <Dropdown.Item
              disabled={payNowLoading}
              icon={
                <BlueDotIcon className="text-blue transition-colors group-hover:text-white" />
              }
              variant="blue"
              onClick={async () => {
                if (payNowLoading) return;
                setPayNowLoading(true);
                try {
                  await updatedProductStore.completeStripeTrialEarly();
                } finally {
                  setPayNowLoading(false);
                }
              }}
            >
              {payNowLoading ? 'Processing…' : 'Upgrade'}
            </Dropdown.Item> */}
            <Button
              variant="primary"
              className="w-full"
              onClick={() => {
                updatedProductStore.openActivateEarlyTrialModal();
              }}
            >
              Activate now
            </Button>
            <Dropdown.Separator />
          </>
        )}
        {/* {userInfo.planType !== 'unlimited' && (
          <>
            <Dropdown.Item
              href={getHomepageUrl('/pricing')}
              icon={
                <BlueDotIcon className="text-blue transition-colors group-hover:text-white" />
              }
              variant="blue"
            >
              {userInfo.planType === 'free'
                ? 'Upgrade'
                : userInfo.planType === 'pro'
                  ? 'Upgrade to Advanced'
                  : 'Unlimited Plan'}
            </Dropdown.Item>

            <Dropdown.Separator />
          </>
        )} */}
        <div className="flex h-10 items-center justify-between pl-2">
          <Text token="14/medium" className="text-tertiary">
            Apps
          </Text>

          <Text
            token="12/medium"
            className="text-gray-90 bg-yellow flex h-6 items-center justify-center rounded-lg px-2"
          >
            New!
          </Text>
        </div>
        <AppItem
          src={`${CDN_BASE}/mascot.jpg`}
          title="Mascot"
          subtitle={'Video character\nreplacement'}
          to={`${getAppletsUrl()}/batch-mascot`}
        />
        {/* <AppItem
          src={`${CDN_BASE}/hermes.jpg`}
          title="Hermes"
          subtitle={'Generative editing &\nblending'}
          to={`${getAppletsUrl()}/motion-editor`}
        /> */}
        <AppItem
          src={`${CDN_BASE}/cartographer.jpg`}
          title="Cartographer"
          subtitle={'Universal motion\nretargeting'}
          to={`${getAppletsUrl()}/cartographer`}
        />
        <Dropdown.Separator />
        <Dropdown.Sub
          text="Theme"
          icon={<LightDarkIcon />}
          zIndex={inModal ? undefined : USER_MENU_DROPDOWN_Z_INDEX}
        >
          <Dropdown.Item
            icon={theme === 'light' ? <CheckmarkIcon /> : null}
            onClick={() => userStore.setTheme('light')}
          >
            Light
          </Dropdown.Item>
          <Dropdown.Item
            icon={theme === 'dark' ? <CheckmarkIcon /> : null}
            onClick={() => userStore.setTheme('dark')}
          >
            Dark
          </Dropdown.Item>
          <Dropdown.Item
            icon={theme === null ? <CheckmarkIcon /> : null}
            onClick={() => userStore.setTheme(null)}
          >
            System
          </Dropdown.Item>
        </Dropdown.Sub>
        <Dropdown.Item
          icon={<LikeIcon />}
          href={getHomepageUrl('/profile/favorites')}
        >
          Favorites
        </Dropdown.Item>
        <Dropdown.Item href={getHomepageUrl('/profile')} icon={<UserIcon />}>
          Account
        </Dropdown.Item>
        <Dropdown.Separator />
        <Dropdown.Item href={getHomepageUrl('/logout')} icon={<LogoutIcon />}>
          Logout
        </Dropdown.Item>
      </div>
    </Dropdown>
  );
}
function AppItem({
  src,
  title,
  subtitle,
  to,
}: {
  src: string;
  title: string;
  subtitle: string;
  to: string;
}) {
  return (
    <a
      href={to}
      className="hover:bg-gray-15 flex h-[70px] items-center gap-4 rounded-lg px-2 transition-colors"
    >
      <img src={src} alt="Mascot" className="h-13 w-8 rounded-sm" />
      <div className="flex flex-col">
        <Text token="14/medium" className="text-primary">
          {title}
        </Text>
        <Text token="14/medium" className="text-tertiary whitespace-pre-line">
          {subtitle}
        </Text>
      </div>
    </a>
  );
}
